/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetAIIntegrationConfigRequest,
  GetAIIntegrationConfigSecurity,
} from "../models/operations/getaiintegrationconfig.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AiIntegrationConfigQueryData,
  buildAiIntegrationConfigQuery,
  prefetchAiIntegrationConfig,
  queryKeyAiIntegrationConfig,
} from "./aiIntegrationConfig.core.js";
export {
  type AiIntegrationConfigQueryData,
  buildAiIntegrationConfigQuery,
  prefetchAiIntegrationConfig,
  queryKeyAiIntegrationConfig,
};

export type AiIntegrationConfigQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getConfig aiIntegrations
 *
 * @remarks
 * Get the org-wide AI integration config for a provider. Returns an empty config (enabled=false, has_api_key=false) when none is set.
 */
export function useAiIntegrationConfig(
  request: GetAIIntegrationConfigRequest,
  security?: GetAIIntegrationConfigSecurity | undefined,
  options?: QueryHookOptions<
    AiIntegrationConfigQueryData,
    AiIntegrationConfigQueryError
  >,
): UseQueryResult<AiIntegrationConfigQueryData, AiIntegrationConfigQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildAiIntegrationConfigQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getConfig aiIntegrations
 *
 * @remarks
 * Get the org-wide AI integration config for a provider. Returns an empty config (enabled=false, has_api_key=false) when none is set.
 */
export function useAiIntegrationConfigSuspense(
  request: GetAIIntegrationConfigRequest,
  security?: GetAIIntegrationConfigSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    AiIntegrationConfigQueryData,
    AiIntegrationConfigQueryError
  >,
): UseSuspenseQueryResult<
  AiIntegrationConfigQueryData,
  AiIntegrationConfigQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAiIntegrationConfigQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setAiIntegrationConfigData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      provider: string;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: AiIntegrationConfigQueryData,
): AiIntegrationConfigQueryData | undefined {
  const key = queryKeyAiIntegrationConfig(...queryKeyBase);

  return client.setQueryData<AiIntegrationConfigQueryData>(key, data);
}

export function invalidateAiIntegrationConfig(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      provider: string;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "aiIntegrations", "getConfig", ...queryKeyBase],
  });
}

export function invalidateAllAiIntegrationConfig(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "aiIntegrations", "getConfig"],
  });
}
